import React from 'react';
import { numberFormatter } from '../../components/formatters/numberFormatter';
import { dateTableFormatter } from '../../dynamic/dateFormatter';

const PrintViewSession = React.forwardRef(({ sessionData }, ref) => {
  if (!sessionData) return null;

  const transactionsByCategory = {};
  let totalSales = 0;
  let totalReturns = 0;
  let hasCartData = false;
 
  // Process transactions - modified to separate by type
  sessionData.transactions?.forEach(transaction => {
    const transactionTotal = parseFloat(transaction.total || 0);
    if (transaction.isOut) totalReturns += transactionTotal;
    else totalSales += transactionTotal;

    if (transaction.cart && transaction.cart.length > 0) {
      hasCartData = true;
      transaction.cart.forEach(cartItem => {
        const categoryName = cartItem.product?.category?.name || 'Uncategorized';
        if (!transactionsByCategory[categoryName]) transactionsByCategory[categoryName] = {};

        // Create unique key that includes both product ID and transaction type
        const transactionType = transaction.isCredit ? 'Credit' : (transaction.isOut ? 'Return' : 'Sale');
        const productKey = `${cartItem.product?.id || cartItem.product?.name || 'Unknown'}_${transactionType}`;

        if (!transactionsByCategory[categoryName][productKey]) {
          transactionsByCategory[categoryName][productKey] = {
            ...cartItem,
            quantity: 0,
            total: 0,
            transactionDates: [],
            type: transactionType, // Single type instead of Set
          };
        }

        const qty = Number(cartItem.quantity) || 0;
        const price = Number(cartItem.price) || 0;

        transactionsByCategory[categoryName][productKey].quantity += qty;
        transactionsByCategory[categoryName][productKey].total += qty * price;
        transactionsByCategory[categoryName][productKey].transactionDates.push(transaction.createdAt);
      });
    }
  });

  const netTotal = totalSales - totalReturns;

  return (
    <div ref={ref} className="print-container">
      {/* Session Info */}
      <div style={{ marginBottom: '20px', fontSize: '14px', paddingLeft: '20px' }}>
        <h2>Session Report</h2>
        <div><strong>Opened At:</strong> {sessionData.openedAt ? dateTableFormatter(sessionData.openedAt) : 'N/A'}</div>
        <div><strong>Closed At:</strong> {sessionData.closedAt ? dateTableFormatter(sessionData.closedAt) : 'Still Open'}</div>
        <div><strong>Session ID:</strong> {sessionData.id}</div>
        <div><strong>User:</strong> {sessionData.created?.name || 'N/A'}</div>
      </div>

      {/* Totals */}
      <div style={{ marginBottom: '20px', fontSize: '14px', paddingLeft: '20px' }}>
        <div><strong>Total Sales:</strong> {numberFormatter(totalSales)}</div>
        <div><strong>Total Returns:</strong> {numberFormatter(totalReturns)}</div>
        <div><strong>Net Total:</strong> {numberFormatter(netTotal)}</div>
      </div>

      {/* Transactions */}
      <div>
        {hasCartData && Object.keys(transactionsByCategory).length > 0 ? (
          Object.entries(transactionsByCategory).map(([category, products]) => {
            // Calculate category total
            const categoryTotal = Object.values(products).reduce((sum, item) => sum + item.total, 0);
            
            return (
              <div key={category} style={{ marginBottom: '25px' }}>
                <h4 style={{ padding: '8px', borderRadius: '4px', backgroundColor: '#fff' }}>{category}</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px', backgroundColor: '#fff' }}>
                  <thead>
                    <tr>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Product</th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>Qty</th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>Total</th>
                      <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>Type</th>
                  
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(products).map((item, index) => (
                      <tr key={index} style={{ backgroundColor: '#fff' }}>
                        <td style={{ border: '1px solid #000', padding: '8px' }}>{item.product?.name || 'Unknown Product'}</td>
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                          {item.quantity}
                        </td>
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>{numberFormatter(item.total)}</td>
                        <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                          {item.type} {/* Single type instead of multiple */}
                        </td>
                  
                      </tr>
                    ))}
                    {/* Category Total Row */}
                    <tr style={{ backgroundColor: '#f9f9f9', fontWeight: 'bold' }}>
                      <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }} colSpan="2">
                        {category} Total:
                      </td>
                      <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>
                        {numberFormatter(categoryTotal)}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }} colSpan="2">
                        
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })
        ) : (
          <div>
            <h4>Transactions ({sessionData.transactions?.length || 0})</h4>
            {sessionData.transactions && sessionData.transactions.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px', backgroundColor: '#fff' }}>
                <thead>
                  <tr>
                    <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>ID</th>
                    <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'left' }}>Type</th>
                    <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>Amount</th>
                    <th style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {sessionData.transactions.map((transaction, index) => (
                    <tr key={index} style={{ backgroundColor: '#fff' }}>
                      <td style={{ border: '1px solid #000', padding: '8px' }}>{transaction.id}</td>
                      <td style={{ border: '1px solid #000', padding: '8px' }}>{transaction.type}</td>
                      <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'right' }}>
                        {transaction.isOut ? `-${numberFormatter(transaction.total)}` : numberFormatter(transaction.total)}
                      </td>
                      <td style={{ border: '1px solid #000', padding: '8px', textAlign: 'center' }}>
                        {dateTableFormatter(transaction.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No transactions found for this session.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default PrintViewSession;